import { Box, Stack, Text, VStack, chakra, useToken } from "@chakra-ui/react";
import {
  DateTimePicker,
  DateTimePickerProps,
  PickersDay,
  PickersDayProps,
} from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { Controller, FieldValues, useFormContext } from "react-hook-form";

type HighlightDate = string;

const getCustomPickersDay =
  (highlights: Record<HighlightDate, { color: string; label: string }> = {}) =>
  (props: PickersDayProps<Dayjs>) => {
    const highlight = highlights[props.day.format("DD/MM/YYYY")];
    const isHighlight = !!highlight;
    const highlightColor = isHighlight ? highlight.color : "transparent";

    return (
      <PickersDay
        {...props}
        sx={{
          border: isHighlight
            ? `1px solid ${useToken("colors", "gray.300")}`
            : "initial",
        }}
      >
        <VStack
          w="full"
          gap={0}
          h="100%"
          alignItems={"center"}
          justifyContent={"center"}
        >
          <Text>{props.day.format("D")}</Text>
          {isHighlight ? (
            <chakra.div
              sx={{
                fontSize: 7,
                color: props.selected ? "white" : highlightColor,
                mt: "-3px",
                borderRadius: "full",
              }}
            >
              {highlight?.label}
            </chakra.div>
          ) : null}
        </VStack>
      </PickersDay>
    );
  };

const TimelineDatepickerFormInput = ({
  timezone,
  label,
  name,
  deps = [],
  dateTimeProps = {},
  transform = {
    input: (val: Date) => dayjs.utc(val),
    output: (val: Dayjs) =>
      val && val.isValid() ? val.toISOString() : undefined,
  },
  highlights = {},
  required = false,
}: {
  timezone: string;
  name: string;
  deps?: string[];
  label: string;
  validate?: (val: Dayjs, otherValues: FieldValues) => boolean | string;
  validationFailMessage?: string;
  dateTimeProps?: DateTimePickerProps<Dayjs>;
  transform?: { input: (val: any) => any; output: (val: any) => any };
  highlights?: Record<HighlightDate, { color: string; label: string }>;
  required?: boolean;
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <Stack w="full" spacing={0}>
      <Text fontWeight={"medium"}>
        {label}
        {required === false ? (
          <chakra.span ml={1} color="gray.400" fontSize={10}>
            (optional)
          </chakra.span>
        ) : (
          ""
        )}
      </Text>
      <Controller
        name={name}
        control={control}
        rules={{ deps }}
        render={({ field, fieldState }) => (
          <DateTimePicker
            {...field}
            value={transform.input(field.value)}
            slotProps={{
              textField: {
                error: fieldState.invalid,
              },
              actionBar: {
                actions: required ? ["accept"] : ["clear", "accept"],
              },
            }}
            slots={{
              day: getCustomPickersDay(highlights),
            }}
            onChange={(date) => {
              field.onChange(transform.output(date));
            }}
            onAccept={(date) => {
              field.onChange(transform.output(date));
            }}
            timezone={timezone}
            closeOnSelect
            {...dateTimeProps}
          />
        )}
      />

      <Box h={4}>
        {errors[name] ? (
          <Text fontSize={"sm"} color="red.300">
            {errors[name].message.toString()}
          </Text>
        ) : null}
      </Box>
    </Stack>
  );
};

export default TimelineDatepickerFormInput;
